import React, { useState, useEffect } from 'react'

// components
import { useTranslation } from 'react-i18next'

import { Grid, Button, useMediaQuery, useTheme } from '@mui/material'
import Loading from '../../components/Loading'
import PageTitle from '../../components/PageTitle'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import { useConfirmationDialogProvider } from '../../context/ConfirmationDialogContext'
import { useDataApi } from '../../context/DataApiContext'

import useStyles from './styles'

// eslint-disable-next-line max-len
import CreateDepotDialog from '../../components/Depots/CreateDepotDialog'
// eslint-disable-next-line max-len
import ModifyDepotDialog from '../../components/Depots/ModifyDepotDialog'

import Table from '../../components/Table'
import generateColumns from './columns'

export default function DepotsList() {
  const { t } = useTranslation()

  const theme = useTheme()
  const fullWidth = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles()
  const { showConfirmationDialog } = useConfirmationDialogProvider()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableState, setTableState] = useState({
    page: 0,
    sort: null,
    filters: null,
  })
  const [tableData, setTableData] = useState(null)
  const [openCreateDepotDialog, setOpenCreateDepotDialog] = useState(false)
  const [openModifyDepotDialog, setOpenModifyDepotDialog] = useState(false)
  const [depotToModify, setDepotToModify] = useState(false)

  const refreshData = (keepPage = false) => {
    if (keepPage) {
      setTableState({ page: tableState.page, sort: null, filters: null })
    } else {
      setTableState({ page: 0, sort: null, filters: null })
    }
  }

  const resource = 'depots'

  useEffect(() => {
    dataProvider
      .getList(
        resource,
        {
          range: { page: tableState.page + 1 },
          filters: tableState.filters,
          sort: tableState.sort,
        },
        false
      )
      .then((response) => {
        setItems(response.data.items)
        setTableData(response.data)
        setLoading(false)
      })
      .catch(() => showNotification('error', t('errors.api-error')))
    // eslint-disable-next-line
  }, [tableState])

  const onModify = (rowIndex) => {
    setDepotToModify(tableData.items[rowIndex])
    setOpenModifyDepotDialog(true)
  }

  const onDelete = (rowIndex) => {
    const { id, name } = tableData.items[rowIndex]
    showConfirmationDialog(
      t('depots-list.confirm-depot-deletion', {
        name,
      }),
      () => {
        dataProvider
          .delete(`depots`, { id })
          .then(() => {
            refreshData()
            showNotification('success', t('depots-list.delete-success'))
          })
          .catch((err) => {
            if (err.response.data.error === 'DEPOT_NOT_FOUND') {
              showNotification('error', t('depots-list.delete-not-found-error'))
            } else if (err.response.data.error === 'OPERATOR_NEEDS_DEPOT') {
              showNotification(
                'error',
                t('depots-list.delete-operator-needs-depot-error')
              )
            } else if (err.response.data.error === 'CHASSIS_NEEDS_DEPOT') {
              showNotification(
                'error',
                t('depots-list.delete-chassis-needs-depot-error')
              )
            } else {
              showNotification('error', t('errors.api-error'))
            }
          })
      },
      t('common.delete')
    )
  }

  const tableColumns = generateColumns({
    t,
    classes,
    items,
    onModify,
    onDelete,
  })

  if (loading) {
    return <Loading />
  }
  return (
    <>
      <CreateDepotDialog
        open={openCreateDepotDialog}
        onClose={() => setOpenCreateDepotDialog(false)}
        onCreated={() => {
          setOpenCreateDepotDialog(false)
          refreshData()
        }}
      />
      <ModifyDepotDialog
        open={openModifyDepotDialog}
        onClose={() => setOpenModifyDepotDialog(false)}
        onModified={() => {
          setOpenModifyDepotDialog(false)
          refreshData()
        }}
        depot={depotToModify}
      />
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs={12} sm={4}>
          <PageTitle title={t('depots-list.title')} />
        </Grid>
        <Grid item container sm={8} justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            fullWidth={fullWidth}
            onClick={() => setOpenCreateDepotDialog(true)}
          >
            {t('depots-list.add-depot')}
          </Button>
        </Grid>
      </Grid>
      <Table
        id={resource}
        columns={tableColumns}
        data={tableData}
        page={tableState.page}
        sort={tableState.sort}
        onChangePage={(currentPage) => {
          setTableState({ ...tableState, page: currentPage })
        }}
        onColumnSortChange={(changedColumn, direction) => {
          const newSort = {
            field: changedColumn,
            direction: direction.toUpperCase(),
          }
          setTableState({ ...tableState, sort: newSort })
        }}
      />
    </>
  )
}
